import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { TextInput } from "../shared/Inputs/TextInput";
import Modal from "../../layouts/Modal";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { IVehicle } from "../../models/Vehicle";
import useVehiclesMutations from "../../data/useVehiclesMutations";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import SelectInput from "../shared/Inputs/SelectInput";
import { NumberInput } from "../shared/Inputs/NumberInput";
import LoadingWheel from "../shared/LoadingWheel";

export default function CreateVehicleModal({ 
  open, 
  setOpen, 
  setMiscVehicle, 
  handleVrmLookup,
  rowIndex
}: { 
  open: boolean; 
  setOpen: Dispatch<SetStateAction<boolean>>; 
  setMiscVehicle: (vehicle: IVehicle) => void; 
  handleVrmLookup: (index: number, vrm: string) => void; 
  rowIndex: number;
}) {
  const { t } = useTranslation("vehicles");
  const [loading, setLoading] = useState(false);
  const { createMisc } = useVehiclesMutations();

  const { register, setValue, handleSubmit, getValues, reset, watch } = useForm();

  const vrm = watch(`vehicles.new.vrm`);

  useEffect(() => {
    if (open) reset();
  }, [open]);

  const onSubmit = (data: any) => {
    setLoading(true);
    createMisc.mutate(data, {
      onSuccess: (response) => {
        setMiscVehicle(response.data);
        setLoading(false);
        setOpen(false);
      },
    });
  }

  return (
    <Modal open={open} setOpen={setOpen} width="max-w-xl">
      <div className="p-4">
        <h2 className="text-lg font-semibold mb-4">{t("Lookup by VIN/ VRM")}</h2>

        <div className="flex items-center justify-between gap-2">
          <TextInput
            register={register}
            registerName={`vehicles.new.vrm`}
            label=""
            // customClasses="min-w-[100px] "
            cols="grow"
          />
          <button
            type="button"
            className="bg-primary-600 hover:bg-primary-700 text-white rounded p-2 flex items-center justify-center disabled:opacity-50 mt-[4px]"
            onClick={() => {
              handleVrmLookup(rowIndex, vrm);
              setOpen(false);
            }}
            aria-label={t("Search VRM")}
          >
            <MagnifyingGlassIcon className="h-[22px] w-[22px]" />
          </button>
        </div>


        <div className="w-full border-b border-gray-400 mb-4 flex items-center justify-center mb-8 mt-6">
          <div className="mb-[-13px] px-4 bg-white text-gray-700">
            {t("Or")}
          </div>
        </div>

        <h2 className="text-lg font-semibold mb-4">{t("Create Vehicle Without VRM")}</h2>
        {/* Form for creating vehicle without VRM */}
        <form onSubmit={handleSubmit(onSubmit)} className={`flex flex-col ${vrm == "" ? "" : "pointer-events-none opacity-50"}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextInput
              register={register}
              registerName={`vehicles.new.make`}
              label={t("Make")}
              customClasses="mb-4"
              required
            />
            <TextInput
              register={register}
              registerName={`vehicles.new.model`}
              label={t("Model")}
              customClasses="mb-4"
              required
            />
          </div>

          <TextInput
              register={register}
              registerName={`vehicles.new.vin`}
              label={t("VIN")}
              customClasses="mb-4"
            />

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <TextInput
              register={register}
              registerName={`vehicles.new.year`}
              label={t("Year")}
              customClasses="mb-4"
            />
            <SelectInput
              register={register}
              registerName={`vehicles.new.fuelType`}
              label={t("Fuel Type")}
              options={[
                { id: 0, name: t("Unspecified") },
                { id: 1, name: t("Petrol") },
                { id: 2, name: t("Diesel") }
              ]}
              setValue={setValue}
              watch={watch}
            />
            <SelectInput
              register={register}
              registerName={`vehicles.new.transmission`}
              label={t("Transmission")}
              options={[
                { id: 0, name: t("Unspecified") },
                { id: 1, name: t("Manual") },
                { id: 2, name: t("Automatic") }
              ]}
              setValue={setValue}
              watch={watch}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <TextInput
              register={register}
              registerName={`vehicles.new.bodyStyle`}
              label={t("Body Style")}
            />
            <TextInput
              register={register}
              registerName={`vehicles.new.colourCurrent`}
              label={t("Colour")}
            />
            <NumberInput
              register={register}
              registerName={`vehicles.new.weight`}
              label={t("Weight")}
            />
            <TextInput
              register={register}
              registerName={`vehicles.new.engineCode`}
              label={t("Engine Code")}
            />
          </div>
          
          {loading ? (<LoadingWheel />) : (
            <ButtonInput 
              label={t("Create Vehicle")} 
              isSubmit={false} 
              classes="w-full" 
              onClick={() => onSubmit({ 
                DVLAMake              : getValues(`vehicles.new.make`         ) == "" ? null : getValues(`vehicles.new.make`),
                DVLAModel             : getValues(`vehicles.new.model`        ) == "" ? null : getValues(`vehicles.new.model`),
                BodyStyle             : getValues(`vehicles.new.bodyStyle`    ) == "" ? null : getValues(`vehicles.new.bodyStyle`),
                ColourCurrent         : getValues(`vehicles.new.colourCurrent`) == "" ? null : getValues(`vehicles.new.colourCurrent`),
                DVLAYearOfManufacture : getValues(`vehicles.new.year`         ) == "" ? null : getValues(`vehicles.new.year`),
                FuelType              : getValues(`vehicles.new.fuelType`     ) == "" ? null : getValues(`vehicles.new.fuelType`),
                GrossWeightKG         : getValues(`vehicles.new.weight`       ) == "" ? null : getValues(`vehicles.new.weight`),
                Transmission          : getValues(`vehicles.new.transmission` ) == "" ? null : getValues(`vehicles.new.transmission`),
                EngineModelCode       : getValues(`vehicles.new.engineCode`   ) == "" ? null : getValues(`vehicles.new.engineCode`),
              })}
            />
          )}
          
        </form>
      </div>
    </Modal>
  );
}